import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Navigation } from './Navigation';
import { SitesMap } from './SitesMap';
import { ObservationsCard } from './ObservationsCard';
import { WorkersCard } from './WorkersCard';
import { StatCard } from './StatCard';
import { InspectionsModal } from './InspectionsModal';
import { IssuesModal } from './IssuesModal';
import { FilterPills } from './FilterPills';
import { FilterBanner } from './FilterBanner';
import { SitesOverviewTable } from './SitesOverviewTable';
import { AnimatedNumber } from './AnimatedNumber';
import { BiltonAIWidget } from './BiltonAIWidget';
import { RiskAreasWidget } from './RiskAreasWidget';
import { PreventionView } from './prevention/PreventionView';
import { SafetyScoreCard } from './SafetyScoreCard';
import { useProjectFilter, AVAILABLE_PROJECTS } from '../contexts/ProjectFilterContext';
import { useContractorFilter, AVAILABLE_CONTRACTORS } from '../contexts/ContractorFilterContext';
import { ProjectSelector } from './ProjectSelector';
import { ContractorSelector } from './ContractorSelector';
import { useDashboardData } from '../hooks/useDashboardData';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { CheckCircle2, AlertCircle, AlertTriangle, FileText, Maximize2, Minimize2, Download, ChevronDown, X, Eye, ClipboardList, Building2, Settings2, LayoutGrid, Layers, Columns, ArrowRight, Search, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from './ui/Card';
import { motion, AnimatePresence } from 'framer-motion';
type IssueType = 'Documents' | 'Inspections' | 'Pre-Shifts' | 'Toolbox Talk' | 'JHA' | 'PTP' | 'all';
type LayoutVariant = 'O' | 'A' | 'B' | 'C' | 'D';
type SectionType = 'overview' | 'compliance' | 'sites' | 'prevention';
export function Dashboard() {
  // Modals & Filters State
  const [isInspectionsModalOpen, setIsInspectionsModalOpen] = useState(false);
  const [isIssuesModalOpen, setIsIssuesModalOpen] = useState(false);
  const [issuesFilterType, setIssuesFilterType] = useState<IssueType>('all');
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  // Layout State
  const [layoutVariant, setLayoutVariant] = useState<LayoutVariant>(() => {
    const saved = localStorage.getItem('dashboard-layout-variant');
    return saved as LayoutVariant || 'B';
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as SectionType | null;
  const [activeTab, setActiveTab] = useState<SectionType>(tabFromUrl || 'prevention');
  // Sync activeTab with URL search params
  useEffect(() => {
    const t = searchParams.get('tab') as SectionType | null;
    if (t && (t === 'compliance' || t === 'prevention')) {
      setActiveTab(t);
    }
  }, [searchParams]);
  const [expandedCard, setExpandedCard] = useState<'compliance' | 'sites' | null>(null); // For Variant D
  const [activeSection, setActiveSection] = useState<SectionType>('overview'); // For Variant A
  const [showSwitcher, setShowSwitcher] = useState(false);
  // Escape key to close expanded map
  useEscapeKey(() => setIsMapExpanded(false), isMapExpanded);
  const handleSetLayoutVariant = (v: LayoutVariant) => {
    setLayoutVariant(v);
    localStorage.setItem('dashboard-layout-variant', v);
  };
  // Refs for ScrollSpy (Variant A & C)
  const overviewRef = useRef<HTMLDivElement>(null);
  const complianceRef = useRef<HTMLDivElement>(null);
  const sitesRef = useRef<HTMLDivElement>(null);
  const {
    selectedProjects
  } = useProjectFilter();
  const {
    selectedContractors
  } = useContractorFilter();
  const data = useDashboardData();
  // ScrollSpy Logic
  useEffect(() => {
    if (layoutVariant !== 'A' && layoutVariant !== 'C') return;
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id as SectionType);
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    if (overviewRef.current) observer.observe(overviewRef.current);
    if (complianceRef.current) observer.observe(complianceRef.current);
    if (sitesRef.current) observer.observe(sitesRef.current);
    return () => observer.disconnect();
  }, [layoutVariant]);
  const scrollToSection = (sectionId: SectionType) => {
    const refs = {
      overview: overviewRef,
      compliance: complianceRef,
      sites: sitesRef
    };
    const ref = refs[sectionId];
    if (ref.current) {
      const yOffset = -140; // Adjust for sticky header
      const y = ref.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  // Data Calculations
  const allProjectsSelected = selectedProjects.length === AVAILABLE_PROJECTS.length;
  const allContractorsSelected = selectedContractors.length === AVAILABLE_CONTRACTORS.length;
  const hasActiveFilters = !allProjectsSelected || !allContractorsSelected;
  const totalDocuments = data.documents.valid + data.documents.expired + data.documents.expiringSoon;
  const totalIssues = data.documents.expired + data.inspections.weekIncomplete + data.preShifts.todayIncomplete + data.toolboxTalk.thisWeekIncomplete + data.jha.thisWeekIncomplete + data.ptp.todayIncomplete;
  const riskScore = 57; // Hardcoded for now, matches SafetyScoreCard
  const totalSites = selectedProjects.length;
  const isSiteFiltered = !allProjectsSelected;
  const isContractorFiltered = !allContractorsSelected;
  const contractorOnlyNote = isContractorFiltered ? 'All sites · Not contractor-specific' : undefined;
  const getDisplayText = () => {
    const parts = [];
    if (!allProjectsSelected) {
      parts.push(selectedProjects.length === 1 ? selectedProjects[0] : `${selectedProjects.length} Sites`);
    } else {
      parts.push('All Sites');
    }
    if (!allContractorsSelected) {
      parts.push(selectedContractors.length === 1 ? selectedContractors[0] : `${selectedContractors.length} Contractors`);
    } else {
      parts.push('All Contractors');
    }
    return parts.join(' · ');
  };
  const openIssuesModal = (type: IssueType) => {
    setIssuesFilterType(type);
    setIsIssuesModalOpen(true);
  };
  // --- RENDER HELPERS FOR CONTENT SECTIONS ---
  const renderOverview = () => <div className="space-y-6" data-id="element-101">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:h-[480px]" data-id="element-102">
        <Card className="h-full flex flex-col p-0 relative overflow-hidden" noPadding data-id="element-103">
          <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1 rounded shadow-sm" data-id="element-104">
            <h2 className="font-bold text-gray-900" data-id="element-105">Sites map</h2>
          </div>
          <button onClick={() => setIsMapExpanded(true)} className="absolute top-4 right-4 z-10 bg-white p-2 rounded shadow-sm hover:bg-gray-50 text-gray-600 transition-colors" title="Expand map" data-id="element-106">
            <Maximize2 size={18} data-id="element-107" />
          </button>
          <SitesMap data-id="element-108" />
        </Card>
        <div className="min-h-[280px] h-full" data-id="element-109">
          <BiltonAIWidget data-id="element-110" />
        </div>
      </div>
    </div>;
  const renderCompliance = () => <div className="space-y-6" data-id="element-111">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-112">
        <SafetyScoreCard data-id="element-113" />
        <WorkersCard data={data.workers} isFiltered={hasActiveFilters} data-id="element-114" />
        <ObservationsCard data={data.observations} isFiltered={hasActiveFilters} data-id="element-115" />
      </div>

      <motion.div key={`row1-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
      opacity: 0.8
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-116">
        <StatCard title="Documents" count={totalDocuments} actionLabel="View all" actionLink="/documents" onSolveIssues={() => openIssuesModal('Documents')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-117">
          <div className="grid grid-cols-3 gap-4 h-full items-center pt-2" data-id="element-118">
            <Link to="/documents?status=valid" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-119">
              <div className="flex items-center justify-center gap-1.5 text-emerald-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-120">
                <CheckCircle2 size={18} data-id="element-121" />
                <AnimatedNumber value={data.documents.valid} className="text-2xl font-bold" data-id="element-122" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-emerald-600 transition-colors" data-id="element-123">
                Valid
              </span>
            </Link>
            <Link to="/documents?status=expired" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-124">
              <div className="flex items-center justify-center gap-1.5 text-red-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-125">
                <AlertCircle size={18} data-id="element-126" />
                <AnimatedNumber value={data.documents.expired} className="text-2xl font-bold" data-id="element-127" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-red-600 transition-colors" data-id="element-128">
                Expired
              </span>
            </Link>
            <Link to="/documents?status=expiring-soon" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-129">
              <div className="flex items-center justify-center gap-1.5 text-amber-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-130">
                <AlertTriangle size={18} data-id="element-131" />
                <AnimatedNumber value={data.documents.expiringSoon} className="text-2xl font-bold" data-id="element-132" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-amber-600 transition-colors" data-id="element-133">
                Expiring soon
              </span>
            </Link>
          </div>
        </StatCard>

        <StatCard title="Inspections & Logs" actionLabel="View all" actionLink="/inspections" onQuickView={() => setIsInspectionsModalOpen(true)} onSolveIssues={() => openIssuesModal('Inspections')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-134">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-135">
            <Link to="/inspections?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-136">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-137">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-138">
                {data.inspections.todayTotal === 0 ? <>
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-139" />
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-140" />
                  </> : <>
                    <AnimatedNumber value={data.inspections.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-141" />
                    <span className="text-lg text-gray-400" data-id="element-142">
                      /<AnimatedNumber value={data.inspections.todayTotal} data-id="element-143" />
                    </span>
                  </>}
              </div>
              <span className="text-xs text-gray-500" data-id="element-144">
                {data.inspections.todayTotal === 0 ? 'No inspections' : 'Incomplete'}
              </span>
            </Link>
            <Link to="/inspections?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-145">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-146">
                Earlier this week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-147">
                <AnimatedNumber value={data.inspections.weekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-148" />
                <span className="text-lg text-gray-400" data-id="element-149">
                  /<AnimatedNumber value={data.inspections.weekTotal} data-id="element-150" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-151">Incomplete</span>
            </Link>
          </div>
        </StatCard>

        <StatCard title="Pre-Shifts" actionLabel="View all" actionLink="/pre-shift" onSolveIssues={() => openIssuesModal('Pre-Shifts')} moreMenuItems={[{
        label: 'Download Pre-Shift report',
        icon: <Download size={16} data-id="element-153" />,
        onClick: () => {}
      }]} isFiltered={hasActiveFilters} data-id="element-152">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-154">
            <Link to="/pre-shift?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-155">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-156">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-157">
                {data.preShifts.todayTotal === 0 ? <>
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-158" />
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-159" />
                  </> : <>
                    <AnimatedNumber value={data.preShifts.todayIncomplete} className="text-2xl font-bold text-red-500" data-id="element-160" />
                    <span className="text-lg text-gray-400" data-id="element-161">
                      /<AnimatedNumber value={data.preShifts.todayTotal} data-id="element-162" />
                    </span>
                  </>}
              </div>
              <span className="text-xs text-gray-500" data-id="element-163">
                {data.preShifts.todayTotal === 0 ? 'No pre-log' : 'Incomplete'}
              </span>
            </Link>
            <Link to="/pre-shift?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-164">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-165">
                Earlier this week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-166">
                <AnimatedNumber value={data.preShifts.weekIncomplete} className="text-2xl font-bold text-red-500" data-id="element-167" />
                <span className="text-lg text-gray-400" data-id="element-168">
                  /<AnimatedNumber value={data.preShifts.weekTotal} data-id="element-169" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-170">Incomplete</span>
            </Link>
          </div>
        </StatCard>
      </motion.div>

      <motion.div key={`row2-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
      opacity: 0.8
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3,
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-171">
        <StatCard title="Toolbox Talk" actionLabel="View all" actionLink="/toolbox-talk" onSolveIssues={() => openIssuesModal('Toolbox Talk')} isFiltered={hasActiveFilters} data-id="element-172">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-173">
            <Link to="/toolbox-talk?period=thisweek&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-174">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-175">
                This week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-176">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-177" />
                <AnimatedNumber value={data.toolboxTalk.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-178" />
                <span className="text-lg text-gray-400" data-id="element-179">
                  /<AnimatedNumber value={data.toolboxTalk.thisWeekTotal} data-id="element-180" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-181">Incomplete</span>
            </Link>
            <Link to="/toolbox-talk?period=previousweek&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-182">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-183">
                Previous week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-184">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-185" />
                <AnimatedNumber value={data.toolboxTalk.previousWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-186" />
                <span className="text-lg text-gray-400" data-id="element-187">
                  /<AnimatedNumber value={data.toolboxTalk.previousWeekTotal} data-id="element-188" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-189">Incomplete</span>
            </Link>
          </div>
        </StatCard>

        <StatCard title="JHA" actionLabel="View all" actionLink="/jha" onSolveIssues={() => openIssuesModal('JHA')} isFiltered={hasActiveFilters} data-id="element-190">
          <div className="flex items-center justify-center h-full pt-2" data-id="element-191">
            <Link to="/jha?period=entireproject&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-4 transition-colors cursor-pointer group w-full max-w-[200px]" data-id="element-192">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-193">
                Entire project(s)
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-194">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-195" />
                <AnimatedNumber value={data.jha.entireProjectsIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-196" />
                <span className="text-lg text-gray-400" data-id="element-197">
                  /<AnimatedNumber value={data.jha.entireProjectsTotal} data-id="element-198" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-199">Incomplete</span>
            </Link>
          </div>
        </StatCard>

        <StatCard title="PTP" actionLabel="View all" actionLink="/ptp" onSolveIssues={() => openIssuesModal('PTP')} isFiltered={hasActiveFilters} data-id="element-200">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-201">
            <Link to="/ptp?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-202">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-203">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-204">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-205" />
                <AnimatedNumber value={data.ptp.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-206" />
                <span className="text-lg text-gray-400" data-id="element-207">
                  /<AnimatedNumber value={data.ptp.todayTotal} data-id="element-208" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-209">Incomplete</span>
            </Link>
            <Link to="/ptp?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-210">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-211">
                Last 7 days
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-212">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-213" />
                <AnimatedNumber value={data.ptp.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-214" />
                <span className="text-lg text-gray-400" data-id="element-215">
                  /<AnimatedNumber value={data.ptp.thisWeekTotal} data-id="element-216" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-217">Incomplete</span>
            </Link>
          </div>
        </StatCard>
      </motion.div>
    </div>;
  const renderSites = () => <SitesOverviewTable isFiltered={hasActiveFilters} isProjectFiltered={!allProjectsSelected} isContractorFiltered={!allContractorsSelected} selectedSites={selectedProjects} data-id="element-218" />;
  // --- ORIGINAL LAYOUT (no sections) ---
  const renderOriginal = () => <div className="space-y-6" data-id="element-219">
      {/* Row 1: Map | Risk Areas | BiltonAI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:h-[480px]" data-id="element-220">
        <Card className="h-full flex flex-col p-0 relative overflow-hidden" noPadding data-id="element-221">
          <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1 rounded shadow-sm" data-id="element-222">
            <h2 className="font-bold text-gray-900" data-id="element-223">Sites map</h2>
          </div>
          <button onClick={() => setIsMapExpanded(true)} className="absolute top-4 right-4 z-10 bg-white p-2 rounded shadow-sm hover:bg-gray-50 text-gray-600 transition-colors" title="Expand map" data-id="element-224">
            <Maximize2 size={18} data-id="element-225" />
          </button>
          <SitesMap data-id="element-226" />
        </Card>
        <div className="min-h-[280px] h-full" data-id="element-227">
          <RiskAreasWidget data-id="element-228" />
        </div>
        <div className="min-h-[280px] h-full" data-id="element-229">
          <BiltonAIWidget data-id="element-230" />
        </div>
      </div>

      {/* Row 2: Safety Score | Workers | Observations */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-231">
        <SafetyScoreCard data-id="element-232" />
        <WorkersCard data={data.workers} isFiltered={hasActiveFilters} data-id="element-233" />
        <ObservationsCard data={data.observations} isFiltered={hasActiveFilters} data-id="element-234" />
      </div>

      {/* Stats Row 1: Documents, Inspections, Pre-Shifts */}
      <motion.div key={`orig-r1-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
      opacity: 0.8
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-235">
        <StatCard title="Documents" count={totalDocuments} actionLabel="View all" actionLink="/documents" onSolveIssues={() => openIssuesModal('Documents')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-236">
          <div className="grid grid-cols-3 gap-4 h-full items-center pt-2" data-id="element-237">
            <Link to="/documents?status=valid" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-238">
              <div className="flex items-center justify-center gap-1.5 text-emerald-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-239">
                <CheckCircle2 size={18} data-id="element-240" />
                <AnimatedNumber value={data.documents.valid} className="text-2xl font-bold" data-id="element-241" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-emerald-600 transition-colors" data-id="element-242">
                Valid
              </span>
            </Link>
            <Link to="/documents?status=expired" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-243">
              <div className="flex items-center justify-center gap-1.5 text-red-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-244">
                <AlertCircle size={18} data-id="element-245" />
                <AnimatedNumber value={data.documents.expired} className="text-2xl font-bold" data-id="element-246" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-red-600 transition-colors" data-id="element-247">
                Expired
              </span>
            </Link>
            <Link to="/documents?status=expiring-soon" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-248">
              <div className="flex items-center justify-center gap-1.5 text-amber-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-249">
                <AlertTriangle size={18} data-id="element-250" />
                <AnimatedNumber value={data.documents.expiringSoon} className="text-2xl font-bold" data-id="element-251" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-amber-600 transition-colors" data-id="element-252">
                Expiring soon
              </span>
            </Link>
          </div>
        </StatCard>
        <StatCard title="Inspections & Logs" actionLabel="View all" actionLink="/inspections" onQuickView={() => setIsInspectionsModalOpen(true)} onSolveIssues={() => openIssuesModal('Inspections')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-253">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-254">
            <Link to="/inspections?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-255">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-256">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-257">
                {data.inspections.todayTotal === 0 ? <>
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-258" />
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-259" />
                  </> : <>
                    <AnimatedNumber value={data.inspections.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-260" />
                    <span className="text-lg text-gray-400" data-id="element-261">
                      /<AnimatedNumber value={data.inspections.todayTotal} data-id="element-262" />
                    </span>
                  </>}
              </div>
              <span className="text-xs text-gray-500" data-id="element-263">
                {data.inspections.todayTotal === 0 ? 'No inspections' : 'Incomplete'}
              </span>
            </Link>
            <Link to="/inspections?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-264">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-265">
                Earlier this week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-266">
                <AnimatedNumber value={data.inspections.weekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-267" />
                <span className="text-lg text-gray-400" data-id="element-268">
                  /<AnimatedNumber value={data.inspections.weekTotal} data-id="element-269" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-270">Incomplete</span>
            </Link>
          </div>
        </StatCard>
        <StatCard title="Pre-Shifts" actionLabel="View all" actionLink="/pre-shift" onSolveIssues={() => openIssuesModal('Pre-Shifts')} moreMenuItems={[{
        label: 'Download Pre-Shift report',
        icon: <Download size={16} data-id="element-272" />,
        onClick: () => {}
      }]} isFiltered={hasActiveFilters} data-id="element-271">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-273">
            <Link to="/pre-shift?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-274">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-275">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-276">
                {data.preShifts.todayTotal === 0 ? <>
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-277" />
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-278" />
                  </> : <>
                    <AnimatedNumber value={data.preShifts.todayIncomplete} className="text-2xl font-bold text-red-500" data-id="element-279" />
                    <span className="text-lg text-gray-400" data-id="element-280">
                      /<AnimatedNumber value={data.preShifts.todayTotal} data-id="element-281" />
                    </span>
                  </>}
              </div>
              <span className="text-xs text-gray-500" data-id="element-282">
                {data.preShifts.todayTotal === 0 ? 'No pre-log' : 'Incomplete'}
              </span>
            </Link>
            <Link to="/pre-shift?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-283">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-284">
                Earlier this week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-285">
                <AnimatedNumber value={data.preShifts.weekIncomplete} className="text-2xl font-bold text-red-500" data-id="element-286" />
                <span className="text-lg text-gray-400" data-id="element-287">
                  /<AnimatedNumber value={data.preShifts.weekTotal} data-id="element-288" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-289">Incomplete</span>
            </Link>
          </div>
        </StatCard>
      </motion.div>

      {/* Stats Row 2: Toolbox Talk, JHA, PTP */}
      <motion.div key={`orig-r2-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
      opacity: 0.8
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3,
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-290">
        <StatCard title="Toolbox Talk" actionLabel="View all" actionLink="/toolbox-talk" onSolveIssues={() => openIssuesModal('Toolbox Talk')} isFiltered={hasActiveFilters} data-id="element-291">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-292">
            <Link to="/toolbox-talk?period=thisweek&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-293">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-294">
                This week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-295">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-296" />
                <AnimatedNumber value={data.toolboxTalk.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-297" />
                <span className="text-lg text-gray-400" data-id="element-298">
                  /<AnimatedNumber value={data.toolboxTalk.thisWeekTotal} data-id="element-299" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-300">Incomplete</span>
            </Link>
            <Link to="/toolbox-talk?period=previousweek&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-301">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-302">
                Previous week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-303">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-304" />
                <AnimatedNumber value={data.toolboxTalk.previousWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-305" />
                <span className="text-lg text-gray-400" data-id="element-306">
                  /<AnimatedNumber value={data.toolboxTalk.previousWeekTotal} data-id="element-307" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-308">Incomplete</span>
            </Link>
          </div>
        </StatCard>
        <StatCard title="JHA" actionLabel="View all" actionLink="/jha" onSolveIssues={() => openIssuesModal('JHA')} isFiltered={hasActiveFilters} data-id="element-309">
          <div className="flex items-center justify-center h-full pt-2" data-id="element-310">
            <Link to="/jha?period=entireproject&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-4 transition-colors cursor-pointer group w-full max-w-[200px]" data-id="element-311">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-312">
                Entire project(s)
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-313">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-314" />
                <AnimatedNumber value={data.jha.entireProjectsIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-315" />
                <span className="text-lg text-gray-400" data-id="element-316">
                  /<AnimatedNumber value={data.jha.entireProjectsTotal} data-id="element-317" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-318">Incomplete</span>
            </Link>
          </div>
        </StatCard>
        <StatCard title="PTP" actionLabel="View all" actionLink="/ptp" onSolveIssues={() => openIssuesModal('PTP')} isFiltered={hasActiveFilters} data-id="element-319">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-320">
            <Link to="/ptp?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-321">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-322">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-323">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-324" />
                <AnimatedNumber value={data.ptp.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-325" />
                <span className="text-lg text-gray-400" data-id="element-326">
                  /<AnimatedNumber value={data.ptp.todayTotal} data-id="element-327" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-328">Incomplete</span>
            </Link>
            <Link to="/ptp?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-329">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-330">
                Last 7 days
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-331">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-332" />
                <AnimatedNumber value={data.ptp.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-333" />
                <span className="text-lg text-gray-400" data-id="element-334">
                  /<AnimatedNumber value={data.ptp.thisWeekTotal} data-id="element-335" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-336">Incomplete</span>
            </Link>
          </div>
        </StatCard>
      </motion.div>

      {/* Sites Overview Table */}
      <SitesOverviewTable isFiltered={hasActiveFilters} isProjectFiltered={!allProjectsSelected} isContractorFiltered={!allContractorsSelected} selectedSites={selectedProjects} data-id="element-337" />
    </div>;
  // --- LAYOUT VARIANTS ---
  const renderVariantA = () => <div className="flex relative" data-id="element-338">
      {/* ScrollSpy Sidebar */}
      <div className="w-12 fixed left-2 top-[200px] bottom-0 flex flex-col items-center py-8 gap-4 bg-transparent z-30" data-id="element-339">
        <button onClick={() => scrollToSection('overview')} className={`p-2.5 rounded-xl transition-all ${activeSection === 'overview' ? 'bg-blue-600 text-white shadow-lg scale-110' : 'bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600 shadow-sm'}`} title="Overview" data-id="element-340">
          <Eye size={18} data-id="element-341" />
        </button>
        <button onClick={() => scrollToSection('compliance')} className={`p-2.5 rounded-xl transition-all ${activeSection === 'compliance' ? 'bg-amber-500 text-white shadow-lg scale-110' : 'bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600 shadow-sm'}`} title="Compliance" data-id="element-342">
          <ClipboardList size={18} data-id="element-343" />
        </button>
        <button onClick={() => scrollToSection('sites')} className={`p-2.5 rounded-xl transition-all ${activeSection === 'sites' ? 'bg-slate-700 text-white shadow-lg scale-110' : 'bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600 shadow-sm'}`} title="Sites at a Glance" data-id="element-344">
          <Building2 size={18} data-id="element-345" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 ml-12 min-w-0 space-y-16" data-id="element-346">
        <div id="overview" ref={overviewRef} className="scroll-mt-[160px]" data-id="element-347">
          <div className="flex items-center gap-4 mb-6" data-id="element-348">
            <div className="w-1.5 h-8 bg-blue-600 rounded-full" data-id="element-349"></div>
            <div data-id="element-350">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-351">
                <Eye className="text-blue-600" data-id="element-352" /> Overview
              </h2>
              <p className="text-sm text-gray-500" data-id="element-353">
                High-level risk and AI insights
              </p>
            </div>
          </div>
          {renderOverview()}
        </div>

        <div id="compliance" ref={complianceRef} className="scroll-mt-[160px]" data-id="element-354">
          <div className="flex items-center gap-4 mb-6" data-id="element-355">
            <div className="w-1.5 h-8 bg-amber-500 rounded-full" data-id="element-356"></div>
            <div data-id="element-357">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-358">
                <ClipboardList className="text-amber-500" data-id="element-359" /> Compliance
              </h2>
              <p className="text-sm text-gray-500" data-id="element-360">
                Detailed breakdown of safety logs and documents
              </p>
            </div>
          </div>
          {renderCompliance()}
        </div>

        <div id="sites" ref={sitesRef} className="scroll-mt-[160px]" data-id="element-361">
          <div className="flex items-center gap-4 mb-6" data-id="element-362">
            <div className="w-1.5 h-8 bg-slate-700 rounded-full" data-id="element-363"></div>
            <div data-id="element-364">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-365">
                <Building2 className="text-slate-700" data-id="element-366" /> Sites at a Glance
              </h2>
              <p className="text-sm text-gray-500" data-id="element-367">
                Comprehensive site health and feature adoption
              </p>
            </div>
          </div>
          {renderSites()}
        </div>
      </div>
    </div>;
  const renderVariantB = () => <div className="space-y-2" data-id="element-368">
      {/* Segmented Control */}
      {/* Context Selectors (Only visible on Compliance tab) */}
      {activeTab === 'compliance' && <div className="sticky top-0 z-30 bg-[#fafafa]/80 backdrop-blur-md py-4 -mx-6 px-6 border-b border-gray-200 flex items-center justify-end" data-id="element-369">
          <div className="flex items-center gap-3" data-id="element-370">
            <ProjectSelector data-id="element-371" />
            <ContractorSelector data-id="element-372" />
          </div>
        </div>}

      {/* Tab Content */}
      <AnimatePresence mode="wait" data-id="element-373">
        <motion.div key={activeTab} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2
      }} data-id="element-374">
          {activeTab === 'overview' && <div className="space-y-6" data-id="element-375">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:h-[480px]" data-id="element-376">
                <Card className="h-full flex flex-col p-0 relative overflow-hidden" noPadding data-id="element-377">
                  <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1 rounded shadow-sm" data-id="element-378">
                    <h2 className="font-bold text-gray-900" data-id="element-379">Sites map</h2>
                  </div>
                  <button onClick={() => setIsMapExpanded(true)} className="absolute top-4 right-4 z-10 bg-white p-2 rounded shadow-sm hover:bg-gray-50 text-gray-600 transition-colors" title="Expand map" data-id="element-380">
                    <Maximize2 size={18} data-id="element-381" />
                  </button>
                  <SitesMap data-id="element-382" />
                </Card>
                <div className="min-h-[280px] h-full" data-id="element-383">
                  <BiltonAIWidget data-id="element-384" />
                </div>
              </div>
            </div>}
          {activeTab === 'compliance' && <div className="space-y-6" data-id="element-385">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-386">
                <SafetyScoreCard data-id="element-387" />
                <WorkersCard data={data.workers} isFiltered={hasActiveFilters} data-id="element-388" />
                <ObservationsCard data={data.observations} isFiltered={hasActiveFilters} data-id="element-389" />
              </div>

              <motion.div key={`row1-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
            opacity: 0.8
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.3
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-390">
                <StatCard title="Documents" count={totalDocuments} actionLabel="View all" actionLink="/documents" onSolveIssues={() => openIssuesModal('Documents')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-391">
                  <div className="grid grid-cols-3 gap-4 h-full items-center pt-2" data-id="element-392">
                    <Link to="/documents?status=valid" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-393">
                      <div className="flex items-center justify-center gap-1.5 text-emerald-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-394">
                        <CheckCircle2 size={18} data-id="element-395" />
                        <AnimatedNumber value={data.documents.valid} className="text-2xl font-bold" data-id="element-396" />
                      </div>
                      <span className="text-xs text-gray-500 group-hover:text-emerald-600 transition-colors" data-id="element-397">
                        Valid
                      </span>
                    </Link>
                    <Link to="/documents?status=expired" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-398">
                      <div className="flex items-center justify-center gap-1.5 text-red-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-399">
                        <AlertCircle size={18} data-id="element-400" />
                        <AnimatedNumber value={data.documents.expired} className="text-2xl font-bold" data-id="element-401" />
                      </div>
                      <span className="text-xs text-gray-500 group-hover:text-red-600 transition-colors" data-id="element-402">
                        Expired
                      </span>
                    </Link>
                    <Link to="/documents?status=expiring-soon" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-403">
                      <div className="flex items-center justify-center gap-1.5 text-amber-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-404">
                        <AlertTriangle size={18} data-id="element-405" />
                        <AnimatedNumber value={data.documents.expiringSoon} className="text-2xl font-bold" data-id="element-406" />
                      </div>
                      <span className="text-xs text-gray-500 group-hover:text-amber-600 transition-colors" data-id="element-407">
                        Expiring soon
                      </span>
                    </Link>
                  </div>
                </StatCard>

                <StatCard title="Inspections & Logs" actionLabel="View all" actionLink="/inspections" onQuickView={() => setIsInspectionsModalOpen(true)} onSolveIssues={() => openIssuesModal('Inspections')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-408">
                  <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-409">
                    <Link to="/inspections?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-410">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-411">
                        Today
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-412">
                        {data.inspections.todayTotal === 0 ? <>
                            <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-413" />
                            <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-414" />
                          </> : <>
                            <AnimatedNumber value={data.inspections.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-415" />
                            <span className="text-lg text-gray-400" data-id="element-416">
                              /
                              <AnimatedNumber value={data.inspections.todayTotal} data-id="element-417" />
                            </span>
                          </>}
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-418">
                        {data.inspections.todayTotal === 0 ? 'No inspections' : 'Incomplete'}
                      </span>
                    </Link>
                    <Link to="/inspections?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-419">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-420">
                        Earlier this week
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-421">
                        <AnimatedNumber value={data.inspections.weekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-422" />
                        <span className="text-lg text-gray-400" data-id="element-423">
                          /<AnimatedNumber value={data.inspections.weekTotal} data-id="element-424" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-425">Incomplete</span>
                    </Link>
                  </div>
                </StatCard>

                <StatCard title="Pre-Shifts" actionLabel="View all" actionLink="/pre-shift" onSolveIssues={() => openIssuesModal('Pre-Shifts')} moreMenuItems={[{
              label: 'Download Pre-Shift report',
              icon: <Download size={16} data-id="element-427" />,
              onClick: () => {}
            }]} isFiltered={hasActiveFilters} data-id="element-426">
                  <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-428">
                    <Link to="/pre-shift?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-429">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-430">
                        Today
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-431">
                        {data.preShifts.todayTotal === 0 ? <>
                            <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-432" />
                            <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-433" />
                          </> : <>
                            <AnimatedNumber value={data.preShifts.todayIncomplete} className="text-2xl font-bold text-red-500" data-id="element-434" />
                            <span className="text-lg text-gray-400" data-id="element-435">
                              /
                              <AnimatedNumber value={data.preShifts.todayTotal} data-id="element-436" />
                            </span>
                          </>}
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-437">
                        {data.preShifts.todayTotal === 0 ? 'No pre-log' : 'Incomplete'}
                      </span>
                    </Link>
                    <Link to="/pre-shift?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-438">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-439">
                        Earlier this week
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-440">
                        <AnimatedNumber value={data.preShifts.weekIncomplete} className="text-2xl font-bold text-red-500" data-id="element-441" />
                        <span className="text-lg text-gray-400" data-id="element-442">
                          /<AnimatedNumber value={data.preShifts.weekTotal} data-id="element-443" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-444">Incomplete</span>
                    </Link>
                  </div>
                </StatCard>
              </motion.div>

              <motion.div key={`row2-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
            opacity: 0.8
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.3,
            delay: 0.1
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-445">
                <StatCard title="Toolbox Talk" actionLabel="View all" actionLink="/toolbox-talk" onSolveIssues={() => openIssuesModal('Toolbox Talk')} isFiltered={hasActiveFilters} data-id="element-446">
                  <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-447">
                    <Link to="/toolbox-talk?period=thisweek&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-448">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-449">
                        This week
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-450">
                        <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-451" />
                        <AnimatedNumber value={data.toolboxTalk.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-452" />
                        <span className="text-lg text-gray-400" data-id="element-453">
                          /
                          <AnimatedNumber value={data.toolboxTalk.thisWeekTotal} data-id="element-454" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-455">Incomplete</span>
                    </Link>
                    <Link to="/toolbox-talk?period=previousweek&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-456">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-457">
                        Previous week
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-458">
                        <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-459" />
                        <AnimatedNumber value={data.toolboxTalk.previousWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-460" />
                        <span className="text-lg text-gray-400" data-id="element-461">
                          /
                          <AnimatedNumber value={data.toolboxTalk.previousWeekTotal} data-id="element-462" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-463">Incomplete</span>
                    </Link>
                  </div>
                </StatCard>

                <StatCard title="JHA" actionLabel="View all" actionLink="/jha" onSolveIssues={() => openIssuesModal('JHA')} isFiltered={hasActiveFilters} data-id="element-464">
                  <div className="flex items-center justify-center h-full pt-2" data-id="element-465">
                    <Link to="/jha?period=entireproject&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-4 transition-colors cursor-pointer group w-full max-w-[200px]" data-id="element-466">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-467">
                        Entire project(s)
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-468">
                        <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-469" />
                        <AnimatedNumber value={data.jha.entireProjectsIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-470" />
                        <span className="text-lg text-gray-400" data-id="element-471">
                          /
                          <AnimatedNumber value={data.jha.entireProjectsTotal} data-id="element-472" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-473">Incomplete</span>
                    </Link>
                  </div>
                </StatCard>

                <StatCard title="PTP" actionLabel="View all" actionLink="/ptp" onSolveIssues={() => openIssuesModal('PTP')} isFiltered={hasActiveFilters} data-id="element-474">
                  <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-475">
                    <Link to="/ptp?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-476">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-477">
                        Today
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-478">
                        <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-479" />
                        <AnimatedNumber value={data.ptp.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-480" />
                        <span className="text-lg text-gray-400" data-id="element-481">
                          /<AnimatedNumber value={data.ptp.todayTotal} data-id="element-482" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-483">Incomplete</span>
                    </Link>
                    <Link to="/ptp?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-484">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-485">
                        Last 7 days
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-486">
                        <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-487" />
                        <AnimatedNumber value={data.ptp.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-488" />
                        <span className="text-lg text-gray-400" data-id="element-489">
                          /<AnimatedNumber value={data.ptp.thisWeekTotal} data-id="element-490" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-491">Incomplete</span>
                    </Link>
                  </div>
                </StatCard>
              </motion.div>

              <SitesOverviewTable isFiltered={hasActiveFilters} isProjectFiltered={!allProjectsSelected} isContractorFiltered={!allContractorsSelected} selectedSites={selectedProjects} data-id="element-492" />
            </div>}
          {activeTab === 'prevention' && <PreventionView data-id="element-493" />}
        </motion.div>
      </AnimatePresence>
    </div>;
  const renderVariantC = () => <div className="space-y-0" data-id="element-494">
      {/* Overview Section */}
      <div className="pb-12" data-id="element-495">
        <div className="sticky top-[132px] z-30 bg-white/95 backdrop-blur-md shadow-sm border-y border-gray-200 py-3 px-6 -mx-6 mb-6 flex items-center justify-between" data-id="element-496">
          <div className="flex items-center gap-3" data-id="element-497">
            <div className="w-1 h-6 bg-blue-600 rounded-full" data-id="element-498"></div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-499">
              <Eye className="text-blue-600" size={20} data-id="element-500" /> Overview
            </h2>
          </div>
          <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full" data-id="element-501">
            Risk Score: {riskScore}/100
          </span>
        </div>
        {renderOverview()}
      </div>

      <div className="h-8 bg-gradient-to-b from-transparent to-gray-50/50 -mx-6" data-id="element-502"></div>

      {/* Compliance Section */}
      <div className="pb-12" data-id="element-503">
        <div className="sticky top-[132px] z-30 bg-white/95 backdrop-blur-md shadow-sm border-y border-gray-200 py-3 px-6 -mx-6 mb-6 flex items-center justify-between" data-id="element-504">
          <div className="flex items-center gap-3" data-id="element-505">
            <div className="w-1 h-6 bg-amber-500 rounded-full" data-id="element-506"></div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-507">
              <ClipboardList className="text-amber-500" size={20} data-id="element-508" /> Compliance
            </h2>
          </div>
          <span className="text-sm font-medium text-amber-700 bg-amber-50 px-3 py-1 rounded-full border border-amber-100" data-id="element-509">
            {totalIssues} open issues
          </span>
        </div>
        {renderCompliance()}
      </div>

      <div className="h-8 bg-gradient-to-b from-transparent to-gray-50/50 -mx-6" data-id="element-510"></div>

      {/* Sites Section */}
      <div className="pb-12" data-id="element-511">
        <div className="sticky top-[132px] z-30 bg-white/95 backdrop-blur-md shadow-sm border-y border-gray-200 py-3 px-6 -mx-6 mb-6 flex items-center justify-between" data-id="element-512">
          <div className="flex items-center gap-3" data-id="element-513">
            <div className="w-1 h-6 bg-slate-700 rounded-full" data-id="element-514"></div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-515">
              <Building2 className="text-slate-700" size={20} data-id="element-516" /> Sites at a
              Glance
            </h2>
          </div>
          <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full" data-id="element-517">
            {totalSites} active sites
          </span>
        </div>
        {renderSites()}
      </div>
    </div>;
  const renderVariantD = () => <div className="space-y-8" data-id="element-518">
      {/* Overview is always visible */}
      <div data-id="element-519">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-520">
          <Eye className="text-blue-600" data-id="element-521" /> Overview
        </h2>
        {renderOverview()}
      </div>

      {/* Expandable Deck */}
      <div className="relative" data-id="element-522">
        <AnimatePresence mode="wait" data-id="element-523">
          {!expandedCard ? <motion.div key="deck" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-524">
              {/* Compliance Preview Card */}
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-t-4 border-t-amber-500" onClick={() => setExpandedCard('compliance')} data-id="element-525">
                <div className="flex justify-between items-start mb-6" data-id="element-526">
                  <div data-id="element-527">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-id="element-528">
                      <ClipboardList className="text-amber-500" data-id="element-529" /> Compliance
                    </h3>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-530">
                      Workers, Observations, and Logs
                    </p>
                  </div>
                  <button className="p-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors" data-id="element-531">
                    <Maximize2 size={18} data-id="element-532" />
                  </button>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 flex items-center justify-between mb-4" data-id="element-533">
                  <span className="text-amber-800 font-semibold" data-id="element-534">
                    Total Open Issues
                  </span>
                  <span className="text-2xl font-bold text-amber-600" data-id="element-535">
                    {totalIssues}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-536">
                  <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full" data-id="element-537">
                    Documents: {data.documents.expired} expired
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full" data-id="element-538">
                    Inspections: {data.inspections.weekIncomplete} missed
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full" data-id="element-539">
                    Pre-Shifts: {data.preShifts.todayIncomplete} missed
                  </span>
                </div>
              </Card>

              {/* Sites Preview Card */}
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-t-4 border-t-slate-700" onClick={() => setExpandedCard('sites')} data-id="element-540">
                <div className="flex justify-between items-start mb-6" data-id="element-541">
                  <div data-id="element-542">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-id="element-543">
                      <Building2 className="text-slate-700" data-id="element-544" /> Sites at a Glance
                    </h3>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-545">
                      Health scores and feature adoption
                    </p>
                  </div>
                  <button className="p-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors" data-id="element-546">
                    <Maximize2 size={18} data-id="element-547" />
                  </button>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between mb-4" data-id="element-548">
                  <span className="text-slate-800 font-semibold" data-id="element-549">
                    Active Sites
                  </span>
                  <span className="text-2xl font-bold text-slate-700" data-id="element-550">
                    {totalSites}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-551">
                  <span className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full border border-emerald-100" data-id="element-552">
                    All sites healthy
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full" data-id="element-553">
                    High adoption rate
                  </span>
                </div>
              </Card>
            </motion.div> : <motion.div key="expanded" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6" data-id="element-554">
              <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-100" data-id="element-555">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3" data-id="element-556">
                  {expandedCard === 'compliance' ? <>
                      <ClipboardList className="text-amber-500" size={28} data-id="element-557" />{' '}
                      Compliance Details
                    </> : <>
                      <Building2 className="text-slate-700" size={28} data-id="element-558" /> Sites
                      Overview
                    </>}
                </h2>
                <button onClick={() => setExpandedCard(null)} className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-559">
                  <Minimize2 size={18} data-id="element-560" /> Collapse
                </button>
              </div>

              {expandedCard === 'compliance' ? renderCompliance() : renderSites()}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
  return <div className="min-h-screen bg-[#fafafa] flex" data-id="element-561">
      <Navigation data-id="element-562" />

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden" data-id="element-563">
        {/* Sticky: Filter Banner + Filter Pills + Solve My Issues Button */}
        <div className={`sticky top-0 z-40 px-6 transition-colors duration-500 ${hasActiveFilters ? 'bg-[#fff3e0]' : 'bg-[#fafafa]'}`} data-id="element-564">
          <FilterBanner data-id="element-565" />
          {!(layoutVariant === 'B' && activeTab === 'prevention') && <div className="py-3" data-id="element-566">
              <div className="flex items-center justify-between gap-4" data-id="element-567">
                <FilterPills data-id="element-568" />
                <button onClick={() => openIssuesModal('all')} className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors shadow-lg whitespace-nowrap" data-id="element-569">
                  <AlertCircle size={20} data-id="element-570" />
                  Solve My Issues
                </button>
              </div>
            </div>}
        </div>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden relative" data-id="element-571">
          <div className="max-w-[1600px] mx-auto px-6 py-3" data-id="element-572">
            <div className="flex items-center justify-between mb-2" data-id="element-573">
              <div data-id="element-574">
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-1.5" data-id="element-575">
                  Dashboard
                  {(activeTab === 'prevention' || activeTab === 'compliance') && <>
                      <span className="text-gray-300 font-normal" data-id="element-576">›</span>
                      <span data-id="element-577">
                        {activeTab === 'prevention' ? 'Prevention' : 'Safety & Compliance'}
                      </span>
                    </>}
                </h1>
                <p className="text-xs text-gray-500" data-id="element-578">
                  {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-579"></div>
            </div>

            {/* Render Selected Layout Variant */}
            <div className="mt-1" data-id="element-580">
              {renderVariantB()}
              <button onClick={() => {
              handleSetLayoutVariant('A');
            }} data-id="element-581" />
            </div>
          </div>
        </main>

        {/* Modals */}
        <InspectionsModal isOpen={isInspectionsModalOpen} onClose={() => setIsInspectionsModalOpen(false)} data-id="element-582" />
        <IssuesModal isOpen={isIssuesModalOpen} onClose={() => setIsIssuesModalOpen(false)} filterType={issuesFilterType} data-id="element-583" />

        {/* Expanded Map Modal */}
        <AnimatePresence data-id="element-584">
          {isMapExpanded && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setIsMapExpanded(false)} data-id="element-585">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            type: 'spring',
            duration: 0.4,
            bounce: 0.2
          }} className="relative w-full h-full max-w-[95vw] max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-586">
                <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg shadow-sm" data-id="element-587">
                  <h2 className="font-bold text-gray-900" data-id="element-588">Sites map</h2>
                </div>
                <button onClick={() => setIsMapExpanded(false)} className="absolute top-4 right-4 z-10 bg-white p-2 rounded-lg shadow-sm hover:bg-gray-50 text-gray-600 transition-colors flex items-center gap-2" title="Close expanded map" data-id="element-589">
                  <Minimize2 size={18} data-id="element-590" />
                </button>
                <div className="w-full h-full" data-id="element-591">
                  <SitesMap data-id="element-592" />
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}